import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Disclaimer } from "@/components/Shared";
import api from "@/lib/api";

export default function StrategyDetail() {
  const { slug } = useParams();
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/strategies/${slug}`)
      .then((r) => setStrategy(r.data))
      .catch(() => setStrategy(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading)
    return <div className="py-32 text-center text-slate-400" data-testid="strategy-loading">Loading framework…</div>;

  if (!strategy)
    return <div className="py-32 text-center text-slate-400" data-testid="strategy-not-found">Framework not found. <Link to="/strategy" className="text-navy underline">Back to Strategy Library</Link></div>;

  const sections = [
    { title: "Concept", body: strategy.concept },
    { title: "Market Conditions", body: strategy.market_conditions || `This framework is most often studied in ${(strategy.condition || "various").toLowerCase()} conditions. Understanding the environment is the first step before applying any tool.` },
    { title: "Components", body: strategy.components || "Each component is studied individually before being combined. No single component is treated as a standalone decision-making system." },
    { title: "Rules", body: strategy.rules || "Rules are defined in advance and followed with discipline. The objective is a repeatable process, not prediction." },
    { title: "Educational Case Study", body: strategy.case_study || "Historical examples are used strictly as hindsight analysis to illustrate the framework. Historical behaviour does not guarantee future outcomes." },
  ];

  return (
    <div data-testid="strategy-detail-page">
      <PageHero
        eyebrow="Strategy & Framework Library"
        title={strategy.name}
        subtitle={strategy.concept}
        crumbs={[{ label: "Strategy Library", to: "/strategy" }, { label: strategy.name }]}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-10">
        {strategy.condition && (
          <span className="inline-flex text-xs font-semibold px-3 py-1.5 rounded-md bg-subtle border border-border text-navy">Best studied in: {strategy.condition}</span>
        )}

        {sections.map((s) => (
          <div key={s.title}>
            <h2 className="font-display text-2xl font-bold text-navy mb-3">{s.title}</h2>
            <p className="text-slate-600 leading-relaxed whitespace-pre-line">{s.body}</p>
          </div>
        ))}

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-5">
            <h4 className="font-display font-semibold text-navy mb-2 inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" />Risk Considerations</h4>
            <p className="text-sm text-slate-600 whitespace-pre-line">{strategy.risk_considerations || "Always define stop-loss, position size and maximum loss before entry. Risk comes before return."}</p>
          </div>
          <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-5">
            <h4 className="font-display font-semibold text-navy mb-2 inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" />Limitations</h4>
            <p className="text-sm text-slate-600 whitespace-pre-line">{strategy.limitations || "No framework works in all conditions. This is educational content, not a guaranteed-profit system."}</p>
          </div>
        </div>

        <Disclaimer />

        <Link to="/strategy" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold transition-colors">
          Back to Strategy Library <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
